import { AsyncThunk, createAsyncThunk } from '@reduxjs/toolkit';
import { logoutSession, setImpersonatedUser, setRedirectUrl, startLogoutSession } from '.';
import { HttpMethods, PayloadTypes, SERVICES, StatusTypes } from '../../services/api';
import { basicApiAsyncThunkCreator, TokenTypes } from '../../services/apiAsyncThunkCreator';
import { cognitoLogout, signInWithCustomProvider } from '../../services/cognito';
import { showErrorMessage, showSuccessMessage, showWarningMessage } from '../notifications';
import { ThunkAPI } from '../rootReducer';
import { IUser } from '../users/user';
import { AccessTypes } from '../../components/common/domain/accountAccessType';
import { getAccountUsageForUser } from './getAccountUsageForUser.thunks';
import { Features, isFeatureEnabled } from '../../featureToggles/features';
import { CORE_APP_URL, HUBSPOT_PORTAL_ID, HUBSPOT_SOCIAL_SIGNUP_FORM_GUID } from '../../config';
import { getCookie } from '../../utils/utils';
import { ILoginOptionsResponse } from './login.thunk.interfaces';

export interface IUserLoginRequest {
  email: string;
  password: string;
  recaptchaToken?: string;
  useRecaptchaFallback?: boolean;
}

export interface IUserLoginResponse {
  token: string;
  userId: string;
  payloadType: PayloadTypes;
  hash: string;
  recaptchaError?: boolean;
}

export interface IUserLoginErrorResponse {
  message: string;
  recaptchaError?: boolean;
}

export interface IUserSingleSignOnRequest {
  email: string;
}

export interface IPreLoginRequest {
  email: string;
}

export interface IRepdataSingleSignLoginWithCode {
  code: string;
}

export interface IUserIdentityProviderResponse {
  id: string;
}

export interface IImpersonateUserRequest {
  authenticatedUser: IUser | { [K in string]: never };
  impersonatedUserId: string;
}

export interface IImpersonateUserResponse {
  token: string;
}

export interface ITwoFactorAuthRequest {
  hash: string;
  code: string;
}

export interface IResendOtpRequest {
  hash: string;
}

export interface IUserSocialLoginRequest {
  token: string;
  recaptchaToken?: string;
  useRecaptchaFallback?: boolean;
}

export const fetchUserById: AsyncThunk<IUser, string, ThunkAPI> = createAsyncThunk(
  'auth/fetchUserById',
  async (userId: string, thunk) => {
    try {
      const response = await basicApiAsyncThunkCreator<IUser, void>({
        endpoint: `users/${userId}`,
        microservice: SERVICES.USERS_SERVICE,
        authenticated: true,
        method: HttpMethods.Get,
        contentType: 'application/json'
      })(undefined, thunk);
      return response;
    } catch (err) {
      return thunk.rejectWithValue(err);
    }
  }
);

export const fetchWhoAmI: AsyncThunk<IUser, void, ThunkAPI> = createAsyncThunk(
  'auth/who-am-i',
  async (_, thunk) => {
    try {
      const response = await basicApiAsyncThunkCreator<IUser, void>({
        endpoint: 'users/who-am-i',
        microservice: SERVICES.USERS_SERVICE,
        authenticated: true,
        method: HttpMethods.Get,
        contentType: 'application/json'
      })(undefined, thunk);

      if ('account' in response && response.account.accessType !== AccessTypes.Enterprise) {
        thunk.dispatch(getAccountUsageForUser({
          accessType: response.account.accessType
        }));
      }
      return response;
    } catch (err) {
      return thunk.rejectWithValue(err);
    }
  }
);

export const userLogin: AsyncThunk<IUserLoginResponse, IUserLoginRequest, ThunkAPI> = createAsyncThunk(
  'auth/userLogin',
  async (loginValues: IUserLoginRequest, thunk) => {
    try {
      const response = await basicApiAsyncThunkCreator<IUserLoginResponse, IUserLoginRequest>({
        endpoint: 'login',
        microservice: SERVICES.AUTH_SERVICE,
        authenticated: false,
        method: HttpMethods.Post,
        contentType: 'application/json'
      })(loginValues, thunk);
      return response;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (StatusTypes.UserInactive === err.status) {
        thunk.dispatch(showWarningMessage('You no longer have an active account. Contact sales@sightx.io to sign up today.'));
        const message = err.message;
        return thunk.rejectWithValue({ message });
      }
      if (StatusTypes.SsoOnlyEnabled === err.status) {
        thunk.dispatch(showErrorMessage('You must use login with SSO option'));
        const message = err.message;
        return thunk.rejectWithValue({ message });
      }
      if (StatusTypes.UpgradeInProgress === err.status) {
        thunk.dispatch(showErrorMessage('Your account is currently upgrading, please wait a few minutes'));
        const message = err.message;
        return thunk.rejectWithValue({ message });
      }

      if (StatusTypes.RecaptchaError === err.status) {
        const message = err.message;
        return thunk.rejectWithValue({ message, recaptchaError: true });
      }
      thunk.dispatch(showErrorMessage('Invalid Credentials'));
      return thunk.rejectWithValue({ message: JSON.stringify(err) });
    }
  }
);

export const userLogout: AsyncThunk<void, void, ThunkAPI> = createAsyncThunk(
  'auth/userLogout',
  async (_: void, thunk) => {
    try {
      document.cookie = 'refresh=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      thunk.dispatch(startLogoutSession());
      const response = await basicApiAsyncThunkCreator<void, void>({
        endpoint: 'logout',
        microservice: SERVICES.AUTH_SERVICE,
        authenticated: true,
        method: HttpMethods.Post,
        contentType: 'application/json'
      })(undefined, thunk);

      try {
        cognitoLogout();
      } catch { }
      thunk.dispatch(logoutSession());
      return response;
    } catch (err) {
      return thunk.rejectWithValue(err);
    }
  }
);

export const impersonateUser: AsyncThunk<IImpersonateUserResponse, IUser, ThunkAPI> = createAsyncThunk(
  'auth/impersonate',
  async (impersonatedUser: IUser, thunk) => {
    try {
      const authState = thunk.getState().auth;
      const loggedInUser = (authState && authState.user) || {};
      const requestData = {
        impersonatedUserId: impersonatedUser.id,
        authenticatedUser: loggedInUser
      };
      const response = await basicApiAsyncThunkCreator<IImpersonateUserResponse, IImpersonateUserRequest>({
        endpoint: 'impersonate',
        microservice: SERVICES.AUTH_SERVICE,
        authenticated: true,
        method: HttpMethods.Post,
        contentType: 'application/json'
      })(requestData, thunk) as IImpersonateUserResponse;
      thunk.dispatch(showSuccessMessage(`Impersonating ${impersonatedUser.firstName} ${impersonatedUser.lastName}`));
      thunk.dispatch(setImpersonatedUser({
        impersonatedUser: impersonatedUser,
        impersonatedToken: response.token
      }));
      return response;
    } catch (err) {
      thunk.dispatch(showErrorMessage('Could not impersonate user'));
      return thunk.rejectWithValue(err);
    }
  }
);

export const twoFactorAuthLogin: AsyncThunk<void, ITwoFactorAuthRequest, ThunkAPI> = createAsyncThunk(
  '2fa/login',
  async (data: ITwoFactorAuthRequest, thunk) => {
    try {
      const response = await basicApiAsyncThunkCreator<void, ITwoFactorAuthRequest>({
        endpoint: '2fa/login',
        microservice: SERVICES.AUTH_SERVICE,
        authenticated: false,
        method: HttpMethods.Post,
        contentType: 'application/json'
      })(data, thunk);
      return response;
    } catch (err) {
      thunk.dispatch(showErrorMessage('Invalid Code'));
      return thunk.rejectWithValue(err);
    }
  }
);

export const resendOtp: AsyncThunk<void, IResendOtpRequest, ThunkAPI> = createAsyncThunk(
  '2fa/resend-challenge',
  async (data: IResendOtpRequest, thunk) => {
    try {
      const response = await basicApiAsyncThunkCreator<void, IResendOtpRequest>({
        endpoint: '2fa/resend-challenge',
        microservice: SERVICES.AUTH_SERVICE,
        authenticated: false,
        method: HttpMethods.Post,
        contentType: 'application/json'
      })(data, thunk);
      thunk.dispatch(showSuccessMessage('a new code has been sent to your email.'));
      return response;
    } catch (err) {
      thunk.dispatch(showErrorMessage('there are a problem, Please wait a few minutes before you try again.'));
      return thunk.rejectWithValue(err);
    }
  }
);

export const userSocialLogin: AsyncThunk<IUserLoginResponse, IUserSocialLoginRequest, ThunkAPI> = createAsyncThunk(
  'auth/userSocialLogin',
  async (loginToken: IUserSocialLoginRequest, thunk) => {
    try {
      const response = await basicApiAsyncThunkCreator<IUserLoginResponse, void>({
        endpoint: 'social-login',
        microservice: SERVICES.AUTH_SERVICE,
        authenticated: false,
        method: HttpMethods.Post,
        contentType: 'application/json',
        cognitoToken: `${TokenTypes.Cognito} ${loginToken.token}`
      })(undefined, thunk);
      thunk.dispatch(showSuccessMessage('Login Successful'));
      return response;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (StatusTypes.UserInactive === err.status) {
        thunk.dispatch(showWarningMessage('You no longer have an active account. Contact sales@sightx.io to sign up today.'));
        const message = err.message;
        return thunk.rejectWithValue(message);
      }
      if (StatusTypes.UpgradeInProgress === err.status) {
        thunk.dispatch(showErrorMessage('Your account is currently upgrading, please wait a few minutes'));
        const message = err.message;
        return thunk.rejectWithValue(message);
      }
      thunk.dispatch(showErrorMessage('Invalid Credentials'));
      return thunk.rejectWithValue(err);
    }
  }
);

export const userSingleSignOn: AsyncThunk<void, IUserSingleSignOnRequest, ThunkAPI> = createAsyncThunk(
  'auth/userSingleSignOn',
  async ({ email }: IUserSingleSignOnRequest, thunk) => {
    try {
      const response = await basicApiAsyncThunkCreator<IUserIdentityProviderResponse, void>({
        endpoint: `users/identity-provider?email=${email}`,
        microservice: SERVICES.USERS_SERVICE,
        authenticated: false,
        method: HttpMethods.Get,
        contentType: 'application/json'
      })(undefined, thunk);

      if ('id' in response) {
        return signInWithCustomProvider(response.id);
      }

      return response;
    } catch (err) {
      thunk.dispatch(showErrorMessage('An error occurred'));
      return thunk.rejectWithValue(err);
    }
  }
);

export const preLoginCheck: AsyncThunk<ILoginOptionsResponse, { email: string }, ThunkAPI> = createAsyncThunk(
  'auth/preLoginCheck',
  async ({ email }: any, thunk) => {
    try {
      const response = await basicApiAsyncThunkCreator<ILoginOptionsResponse, { email: string }>({
        endpoint: 'login-precheck',
        microservice: SERVICES.AUTH_SERVICE,
        authenticated: false,
        method: HttpMethods.Post,
        contentType: 'application/json'
      })({ email }, thunk);

      const sanitizedResponse: ILoginOptionsResponse = {
        options: []
      };

      if ('options' in response) {
        const { options = [] } = response;
        sanitizedResponse.options.push(...options);
      }

      return sanitizedResponse;
    } catch (err) {
      thunk.dispatch(showErrorMessage('An error occurred'));
      return thunk.rejectWithValue(err);
    }
  }
);

const signInWithCustomProvideRepdata = (url: string): { redirectURL: string } => {
  window.location.href = url;
  return { redirectURL: url };
};

export const repdatatUserSingleSignOn: AsyncThunk<{ redirectURL: string }, { email: string }, ThunkAPI> = createAsyncThunk(
  'auth/repdatatUserSingleSignOn',
  async ({ email }: any, thunk) => {
    try {
      const response = await basicApiAsyncThunkCreator<{ redirectURL: string }, { email: string }>({
        endpoint: 'repdata-sso-redirect',
        microservice: SERVICES.AUTH_SERVICE,
        authenticated: false,
        method: HttpMethods.Post,
        contentType: 'application/json'
      })({ email }, thunk);

      if ('redirectURL' in response && typeof response.redirectURL === 'string') {
        return signInWithCustomProvideRepdata(response.redirectURL);
      }

      return response;
    } catch (err) {
      thunk.dispatch(showErrorMessage('An error occurred'));
      return thunk.rejectWithValue(err);
    }
  }
);

export const repdatatUserSingleSignOnLogin: AsyncThunk<void, { code: string }, ThunkAPI> = createAsyncThunk(
  'auth/repdatatUserSingleSignOnLogin',
  async ({ code }, thunk) => {

    try {
      const response = await basicApiAsyncThunkCreator<void, { code: string }>({
        endpoint: 'repdata-sso-login',
        microservice: SERVICES.AUTH_SERVICE,
        authenticated: false,
        method: HttpMethods.Post,
        contentType: 'application/json'
      })({ code }, thunk);

      return response;
    } catch (err) {
      thunk.dispatch(showErrorMessage('An error occurred'));
      return thunk.rejectWithValue(err);
    }
  }
);

export interface IUserSocialSignUpPayload {
  token: string;
  accessType: AccessTypes;
}

interface IUserSocialSignUpResponse {
  token: string;
  userId: string;
  redirectToCore?: boolean;
}

export const userSocialSignUpPro: AsyncThunk<IUserSocialSignUpResponse, IUserSocialSignUpPayload, ThunkAPI> = createAsyncThunk(
  'auth/userSingleSignOn',
  async (data, thunk) => {
    try {
      const { token, accessType } = data;
      const response = await basicApiAsyncThunkCreator<IUserSocialSignUpResponse, Pick<IUserSocialSignUpPayload, 'accessType'>>({
        endpoint: 'signUp/social-user',
        microservice: SERVICES.AUTH_SERVICE,
        authenticated: false,
        method: HttpMethods.Post,
        contentType: 'application/json',
        cognitoToken: `${TokenTypes.Cognito} ${token}`
      })({
        accessType
      }, thunk);
      if (HUBSPOT_PORTAL_ID && HUBSPOT_SOCIAL_SIGNUP_FORM_GUID) {
        const { given_name: firstname = '', family_name: lastname = '', email } = JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
        const formData = {
          fields: [{ name: 'email', value: email }, { name: 'firstname', value: firstname }, { name: 'lastname', value: lastname }],
          context: { hutk: getCookie('hubspotutk') }
        };
        const request = new Request(`https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_SOCIAL_SIGNUP_FORM_GUID}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
        fetch(request);
      }
      if ('redirectToCore' in response && response.redirectToCore && isFeatureEnabled(Features.PlgExternal2)) {
        thunk.dispatch(setRedirectUrl({ url: `${CORE_APP_URL}?registered=true` }));
      }
      return response;
    } catch (err) {
      thunk.dispatch(showErrorMessage('An error occurred'));
      return thunk.rejectWithValue(err);
    }
  }
);
